import type { ReactNode } from "react";

import { cn } from "~/lib/utils";
import { COLLAPSED_SIDEBAR_TITLEBAR_INSET_CLASS } from "~/workspaceTitlebar";
import { SidebarInset } from "../ui/sidebar";

export function MorrowWorkspacePage({
  title,
  subtitle,
  icon,
  children,
  action,
  actionClassName,
  compactHeader = false,
  toolbar,
  toolbarClassName,
  footer,
  wide = false,
  contentClassName,
}: {
  readonly title: string;
  readonly subtitle?: string;
  readonly icon: ReactNode;
  readonly children: ReactNode;
  readonly action?: ReactNode;
  readonly actionClassName?: string;
  /** Title and `action` share one row (e.g. compact header filters). */
  readonly compactHeader?: boolean;
  readonly toolbar?: ReactNode;
  readonly toolbarClassName?: string;
  readonly footer?: ReactNode;
  readonly wide?: boolean;
  readonly contentClassName?: string;
}) {
  const contentInsetClass = cn(
    "w-full min-w-0 px-4 sm:px-6",
    COLLAPSED_SIDEBAR_TITLEBAR_INSET_CLASS,
  );

  return (
    <SidebarInset className="h-dvh min-h-0 overflow-hidden overscroll-y-none bg-background text-foreground isolate">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="flex min-h-[var(--workspace-topbar-height)] shrink-0 items-center border-b border-border">
          <div
            className={cn(
              "flex min-w-0 gap-2 py-2.5 sm:gap-3",
              compactHeader
                ? "flex-col items-stretch gap-2.5 sm:flex-row sm:items-center sm:gap-3"
                : "flex-col sm:flex-row sm:items-center sm:py-3",
              contentInsetClass,
            )}
          >
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <span className="flex size-8 shrink-0 items-center justify-center text-muted-foreground [&_img]:size-8 [&_img]:rounded-md [&_img]:object-cover [&_svg]:size-6">
                {icon}
              </span>
              <div className="min-w-0 flex-1">
                <h1 className="truncate text-sm font-semibold tracking-tight text-foreground">
                  {title}
                </h1>
                {subtitle ? (
                  <p className="truncate text-xs text-muted-foreground">{subtitle}</p>
                ) : null}
              </div>
            </div>
            {action ? (
              <div
                className={cn(
                  "flex min-w-0 justify-end",
                  compactHeader
                    ? "w-full min-w-0 sm:w-auto sm:flex-[1_0_auto]"
                    : "w-full shrink-0 sm:w-auto",
                  actionClassName,
                )}
              >
                {action}
              </div>
            ) : null}
          </div>
        </header>
        {toolbar ? (
          <div className={cn("shrink-0 border-b border-border", toolbarClassName)}>
            <div className={cn("py-3", contentInsetClass)}>{toolbar}</div>
          </div>
        ) : null}
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-y-contain">
          <div
            className={cn(
              "flex flex-col gap-6 py-6 motion-reduce:animate-none animate-morrow-page-enter",
              contentInsetClass,
              contentClassName,
            )}
          >
            {children}
          </div>
        </div>
        {footer ? (
          <div className="shrink-0 border-t border-border">
            <div className={cn("py-3", contentInsetClass)}>{footer}</div>
          </div>
        ) : null}
      </div>
    </SidebarInset>
  );
}
